import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { Socket } from 'socket.io-client';
import {
  HELLO_EVENT, TABLE_FOCUS, TABLE_JOIN, TABLE_MEMBERS, TABLE_OPENED, TABLE_ROLE, TABLE_REMOVED, TABLE_SCENE, TABLE_SESSION, TABLE_SWITCH, TABLE_SYNC, TABLE_NOTEBOOK, TABLE_HOARDS,
  memberLabel, parseHello, parseMembers, parseTableRemoved, roleWord, type HelloMessage, type Role, type Snapshot, type TableMember, type TableView, type UserSettings, type ViewId,
  parsePowers, type Power,
} from '@dragons-table/vtt-shared';
import { Board } from './Board';
import { TableStateProvider, boardRole } from '../table/TableState';
import { fetchSettings, saveSettings } from '../layout';
import {
  connectSocket, exchangeCode, fetchMe, hasSession, onSessionEnded, sessionEnded, setTokens, siteTableUrl, siteUrl, takeHandoffCode, type SessionUser,
  UNREACHABLE,
} from '../session';
import { Onboarding } from '../shell/Onboarding';
import { Shell } from '../shell/Shell';
import { claimWatch, fetchTable, keepWatchToken, openPractice, openTable, refusalWords, setSessionState, tableOfParty, takeWatchToken, type TableResult } from '../table';
import { StreamView } from '../shell/StreamView';
import { notebookChanged } from '../notebook';
import { hoardsChanged } from '../loot';
import { fetchAccess } from '../access';
import { onLocked } from '../lock';
import { EarlyAccess } from './EarlyAccess';
import { setAnalyticsSystem, tableOpened } from '../analytics';
import { diagnosticsJoined, watchDiagnostics } from '../diagnostics';
import { PracticeTracker } from '../shell/panels/ChecklistPanel';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Module-level, so its identity is stable: as an inline default parameter it
// was a new function on every render, the arrival effect depended on it, and
// the page re-ran the exchange (or the redirect) on every render — a loop
// that hammered both origins until nginx's shared zone answered 429 to the
// next boot on this address (found by the first deployed run of the spec).
export const defaultRedirect = (url: string): void => window.location.replace(url);

type Arrival =
  | { state: 'arriving' }
  | { state: 'seated'; user: SessionUser }
  // Early access (temporary): signed in, but the tabletop is not unlocked for this account yet.
  | { state: 'locked' }
  | { state: 'refused' }
  // The tabletop's server did not answer: said, with a retry, and never a hop to the site (it would bounce straight back).
  | { state: 'unreachable' };

type Seat =
  | { state: 'loading' }
  | { state: 'at'; view: TableView }
  | { state: 'not-found' }
  // Session Active? off (qa-runs 1429): a player or watcher waits outside, told nothing of the table.
  | { state: 'waiting' }
  | { state: 'removed'; name: string; gone?: boolean }
  | { state: 'error' };

/**
 * A table's address (slices 02 to 05). Arriving with a handoff code trades
 * it for a session of this origin; arriving with a session confirms it;
 * arriving with neither goes to the site's door for this table. Seated, the
 * page reads the table: who is at it (live from the site), whether the DM
 * has opened it, everyone's identity colour — and joins the socket room, so
 * a seat that ends on the site closes the table here with a notice. Before
 * the first table loads, the person is told once that the arrangement is
 * theirs (slice 05); an open table renders as the shell. `?view=second` is
 * the same person's second window, with its own socket (design/21 §5.4).
 */
/** In front of the person: the tab showing and the window focused (issue 1182). */
export const pageFocused = (): boolean => document.visibilityState === 'visible' && document.hasFocus();

export function Table({ redirect = defaultRedirect }: { redirect?: (url: string) => void }) {
  const { campaignId = '' } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { search } = useLocation();
  const view: ViewId = params.get('view') === 'second' ? 'second' : 'main';
  const [arrival, setArrival] = useState<Arrival>({ state: 'arriving' });
  const [hello, setHello] = useState<HelloMessage | null | 'pending' | 'retrying'>('pending');
  const [seat, setSeat] = useState<Seat>({ state: 'loading' });
  const [notice, setNotice] = useState('');
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [joined, setJoined] = useState<{ role: Role; powers?: Power[]; snapshot: Snapshot | null } | null>(null);
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [ended, setEnded] = useState(sessionEnded);
  const [watchRefused, setWatchRefused] = useState(false);
  // Early access (temporary): each unlock arrives again, now with the tabletop open to this account.
  const [unlocks, setUnlocks] = useState(0);
  useEffect(() => onSessionEnded(() => setEnded(true)), []);
  useEffect(() => onLocked(() => setArrival({ state: 'locked' })), []);
  const validId = UUID_RE.test(campaignId);
  // Every beacon from this table is tagged with its game system (issue 1601); none once the page leaves it.
  const tableSystem = seat.state === 'at' ? seat.view.campaignSystem : null;
  useEffect(() => { setAnalyticsSystem(tableSystem); return () => setAnalyticsSystem(null); }, [tableSystem]);

  const applyResult = useCallback((r: TableResult) => {
    if (r.kind === 'ok') { setSeat({ state: 'at', view: r.view }); setNotice(''); }
    else if (r.kind === 'not-found') setSeat({ state: 'not-found' });
    else if (r.kind === 'refused' && r.error === 'waiting') { setSeat({ state: 'waiting' }); setNotice(''); }
    // A refusal before the table was ever read (a 500, a 429) leaves nothing to show it over: said as the table not
    // reached, with a retry, rather than "Finding the table…" for ever.
    else if (r.kind === 'refused') { setNotice(refusalWords(r)); setSeat((s) => (s.state === 'loading' ? { state: 'error' } : s)); }
    else setSeat({ state: 'error' });
  }, []);

  // An old party address (issue 1578): a bookmark, invitation or notification that named a party's table lands on its
  // campaign's, the rest of the query kept and a kept watch token carried along; asked only when the address is not a
  // table, so a campaign's own address costs nothing more. Anything else reads as today's not-found. True when it moved.
  // The arrival and Try again both come through here, so a first read that failed still forwards an old link.
  const movedTo = useCallback(async (watch: string | null, live: () => boolean = () => true): Promise<boolean> => {
    const moved = await tableOfParty(campaignId, watch);
    if (!moved || !live()) return false;
    const query = new URLSearchParams(search);
    if (watch) query.set('watch', watch);
    const rest = query.toString();
    navigate(`/t/${moved}${rest ? `?${rest}` : ''}`, { replace: true });
    return true;
  }, [campaignId, search, navigate]);
  /** The table read, or the hop to the campaign an old party address names. */
  const readTable = useCallback(async (watch: string | null, live: () => boolean = () => true): Promise<TableResult | null> => {
    const result = await fetchTable(campaignId);
    if (!live()) return null;
    if (result.kind === 'not-found' && !watch && await movedTo(null, live)) return null;
    return result;
  }, [campaignId, movedTo]);

  useEffect(() => {
    if (!validId) return undefined;
    let live = true;
    (async () => {
      // A watch link (issue 1040): its token kept in this tab before any hop to the site's door, which drops the query.
      keepWatchToken(campaignId);
      const code = takeHandoffCode();
      const user = code ? await exchangeCode(code) : hasSession() ? await fetchMe() : null;
      if (!live) return;
      if (user === UNREACHABLE) { setArrival({ state: 'unreachable' }); return; }
      if (!user) {
        setArrival({ state: 'refused' });
        redirect(siteTableUrl(campaignId));
        return;
      }
      // Early access (temporary): an account that has not entered the key sees the screen, and nothing else is read.
      const access = await fetchAccess();
      if (!live) return;
      if (access.gate && !access.unlocked) { setArrival({ state: 'locked' }); return; }
      // Claimed once the session exists; a member's claim changes nothing. A link no longer live seats nobody. An old
      // party address is forwarded to its campaign's (`movedTo`, `readTable`).
      const isLive = () => live;
      const watch = takeWatchToken(campaignId);
      if (watch && !(await claimWatch(campaignId, watch))) {
        if (await movedTo(watch, isLive)) return;
        setWatchRefused(true);
      }
      if (!live) return;
      const [result, prefs] = await Promise.all([readTable(watch, isLive), fetchSettings()]);
      if (!live || !result) return;
      setArrival({ state: 'seated', user });
      // A settings read that failed must not hold the table: treat the statement as seen for this visit.
      setSettings(prefs ?? { onboardingSeen: true, offerPresets: true, confirmDelete: true });
      applyResult(result);
    })();
    return () => {
      live = false;
    };
  }, [campaignId, validId, redirect, applyResult, unlocks, movedTo, readTable]);

  useEffect(() => {
    if (arrival.state !== 'seated') return undefined;
    const socket: Socket = connectSocket();
    setSocket(socket);
    const unwatchDiagnostics = watchDiagnostics(socket, campaignId);
    socket.on('connect', () => setConnected(true));
    // Whether this table is in front of the person (issue 1182): the server sends no push to someone already looking.
    const tellFocus = () => { if (socket.connected) socket.emit(TABLE_FOCUS, { campaignId, focused: pageFocused() }); };
    document.addEventListener('visibilitychange', tellFocus);
    window.addEventListener('focus', tellFocus);
    window.addEventListener('blur', tellFocus);
    socket.on('disconnect', () => setConnected(false));
    // Every connection — the first and each return — joins afresh and is
    // answered with a snapshot projected for this person; the board starts
    // from it, never from a replay.
    const join = () => {
      socket.emit(TABLE_JOIN, { campaignId }, (reply: { ok?: true; role?: Role; powers?: unknown; snapshot?: Snapshot | null; error?: string }) => {
        // Session Active? off (qa-runs 1429): the waiting screen, until the DM ticks it.
        if (reply && reply.error === 'waiting') { setJoined(null); setSeat({ state: 'waiting' }); return; }
        if (reply && reply.ok) diagnosticsJoined();
        if (reply && reply.ok && reply.role) tableOpened(campaignId, reply.role);
        if (reply && reply.ok && reply.role) setJoined({ role: reply.role, ...(reply.role === 'codm' ? { powers: parsePowers(reply.powers) ?? [] } : {}), snapshot: reply.snapshot ?? null });
        if (reply && reply.ok) tellFocus();
      });
    };
    socket.on(HELLO_EVENT, (raw: unknown) => {
      setHello(parseHello(raw));
      join();
    });
    // Session Active? (qa-runs 1429). The DM's and co-DMs' windows keep the check and the waiting count; a waiting
    // window, ticked, joins and reads the table now (the DM's status with the join's snapshot, #315); a player's, unticked,
    // goes back to the waiting screen at once — the server has already taken them off the table.
    socket.on(TABLE_SESSION, async (raw: unknown) => {
      const n = raw as { campaignId?: unknown; active?: unknown; waiting?: unknown } | null;
      if (!n || n.campaignId !== campaignId || typeof n.active !== 'boolean') return;
      if (typeof n.waiting === 'number') { setSessionState({ active: n.active, waiting: n.waiting }); return; }
      if (n.active) { join(); applyResult(await fetchTable(campaignId)); return; }
      setJoined(null);
      setSeat({ state: 'waiting' });
    });
    socket.on(TABLE_MEMBERS, (raw: unknown) => {
      const members = parseMembers(raw);
      if (!members) return;
      setSeat((s) => (s.state === 'at' ? { state: 'at', view: withMembers(s.view, members) } : s));
    });
    // Someone who waited for the DM joined before there was a scene, and the DM's arrival was told to a board not yet
    // there (qa-runs 140): the board starts from a snapshot read now the table is open, never the one from that join.
    // Not waited on: a sync never answered (the connection gone) leaves the page to its next join, never shut.
    socket.on(TABLE_OPENED, async () => {
      socket.emit(TABLE_SYNC, { campaignId }, (snap: Snapshot | { error: string }) => {
        if (snap && 'state' in snap) setJoined((j) => (j ? { ...j, snapshot: snap } : j));
      });
      applyResult(await fetchTable(campaignId));
    });
    // A handover while connected: this person's role changed, and with it their projection — a fresh snapshot as the new role.
    // A co-DM re-ticked (issue 1159) is told the same way, with their powers; the table's view is read again for its menus.
    socket.on(TABLE_ROLE, async (raw: unknown) => {
      const r = raw as { campaignId?: unknown; role?: unknown; powers?: unknown; snapshot?: Snapshot | null } | null;
      if (!r || r.campaignId !== campaignId || !(r.role === 'dm' || r.role === 'player' || r.role === 'watcher' || r.role === 'codm')) return;
      setJoined({ role: r.role, ...(r.role === 'codm' ? { powers: parsePowers(r.powers) ?? [] } : {}), snapshot: r.snapshot ?? null });
      applyResult(await fetchTable(campaignId));
    });
    // The scene changed (the map): a shared fact, re-read rather than trusted from the wire.
    socket.on(TABLE_SCENE, async () => applyResult(await fetchTable(campaignId)));
    // A player's shared notebook pages changed (issue 1166; sent to the DM alone, no word of a page): the open view reads them again.
    socket.on(TABLE_NOTEBOOK, (raw: unknown) => { if ((raw as { campaignId?: unknown } | null)?.campaignId === campaignId) notebookChanged(campaignId); });
    // A held hoard changed (qa-runs 1197; sent to the DM and co-GMs alone, no word of a hoard): the Loot tool reads its list again.
    socket.on(TABLE_HOARDS, (raw: unknown) => { if ((raw as { campaignId?: unknown } | null)?.campaignId === campaignId) hoardsChanged(campaignId); });
    // The players moved to another scene (issue 993): its map arrives WITH its state, and both land in one update, so the
    // board never asks for the old scene's map at the new scene's fog version. The re-read that follows confirms it.
    socket.on(TABLE_SWITCH, (raw: unknown) => {
      const msg = raw as { campaignId?: unknown; snapshot?: Snapshot | null } | null;
      if (!msg || msg.campaignId !== campaignId || !msg.snapshot || msg.snapshot.map === undefined || msg.snapshot.scene?.live === false) return;
      const map = msg.snapshot.map;
      setSeat((s) => (s.state === 'at' && s.view.map?.id !== map?.id ? { state: 'at', view: { ...s.view, map } } : s));
    });
    socket.on(TABLE_REMOVED, (raw: unknown) => {
      const gone = parseTableRemoved(raw);
      if (gone && gone.campaignId === campaignId) setSeat({ state: 'removed', name: gone.name, gone: gone.gone === true });
    });
    // A failed attempt socket.io will make again (the network, the server restarting) is not a refusal: only one it has
    // given up on is (the handshake refused for good).
    socket.on('connect_error', () => setHello(socket.active ? 'retrying' : null));
    return () => {
      setSessionState(null);
      unwatchDiagnostics();
      document.removeEventListener('visibilitychange', tellFocus);
      window.removeEventListener('focus', tellFocus);
      window.removeEventListener('blur', tellFocus);
      socket.close();
      setSocket(null);
    };
  }, [arrival.state, campaignId, applyResult]);

  // The practice table (qa-runs 1163): the DM pressed Try the tabletop to come here, so it opens with its ready-made
  // scene without a second press. Any other table waits for Open table.
  const practiceToOpen = arrival.state === 'seated' && seat.state === 'at' && seat.view.practice === true && seat.view.me.role === 'dm' && !seat.view.opened;
  useEffect(() => {
    if (!practiceToOpen) return;
    let gone = false;
    void openPractice(campaignId).then((r) => { if (!gone) applyResult(r); });
    return () => { gone = true; };
  }, [practiceToOpen, campaignId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!validId) {
    return <main className="min-h-screen bg-stone-950 text-stone-100 p-6"><p role="alert">That is not a table address.</p></main>;
  }

  if (arrival.state === 'locked') return <EarlyAccess onUnlocked={() => { setArrival({ state: 'arriving' }); setUnlocks((n) => n + 1); }} />;

  // The statement lies over the arrival page for a person with a seat here, and the shell waits on it.
  const statement = arrival.state === 'seated' && seat.state === 'at' && settings !== null && !settings.onboardingSeen;
  const open = arrival.state === 'seated' && seat.state === 'at' && seat.view.opened;
  // Said once, over everything, when a refresh was refused (issue 793): every
  // later call would fail the same way, so the calls themselves stay quiet.
  const endedNotice = ended && arrival.state === 'seated' ? (
    <div role="alert" data-testid="session-ended" className="fixed inset-x-0 top-0 z-50 bg-red-900 px-4 py-2 text-center text-stone-100">
      Your session has ended. Reload the table to continue.
    </div>
  ) : null;

  if (open && socket && joined && settings && !statement) {
    return (
      <>
      {endedNotice}
      <TableStateProvider socket={socket} campaignId={campaignId} role={boardRole(joined.role, joined.powers)} powers={joined.role === 'codm' ? joined.powers ?? [] : null} meId={seat.view.me.id} snapshot={joined.snapshot} connected={connected}>
        {seat.view.practice && joined.role === 'dm' && <PracticeTracker campaignId={campaignId} />}
        {joined.role === 'watcher' ? (
          // A watcher's stream view (issue 1040, COMM-010): the board and the turn order, nothing to change anything with.
          <StreamView campaignId={campaignId} table={seat.view} onResult={applyResult} settings={settings} onSettings={setSettings} board={<Board />} />
        ) : (
          <Shell view={view} campaignId={campaignId} table={seat.view} onResult={applyResult} settings={settings} onSettings={setSettings} siteUrl={siteUrl()}
            board={view === 'main' ? <Board /> : null} />
        )}
      </TableStateProvider>
      </>
    );
  }

  return (
    <main className="min-h-screen bg-stone-950 text-stone-100 flex items-center justify-center p-6">
      {endedNotice}
      <section className="max-w-xl w-full rounded-lg border border-stone-800 bg-stone-900 p-6 space-y-4">
        <h1 className="text-2xl font-semibold" data-testid="table-name">
          {seat.state === 'at' ? seat.view.name : 'The table'}
        </h1>
        <p data-testid="arrival" className="text-stone-300">
          {arrival.state === 'arriving' && 'Arriving…'}
          {arrival.state === 'refused' && 'Going to the site to sign in…'}
          {arrival.state === 'unreachable' && 'The tabletop is not answering just now, so the table cannot open. Try again in a moment.'}
          {arrival.state === 'seated' && (arrival.user.handle ? `Signed in as @${arrival.user.handle}` : 'Signed in, no handle chosen yet')}
        </p>
        <p data-testid="socket" className="text-stone-400 text-sm">
          {arrival.state !== 'seated' && '—'}
          {arrival.state === 'seated' && hello === 'pending' && 'Connecting…'}
          {arrival.state === 'seated' && hello === 'retrying' && 'The table is not answering yet; still trying to connect…'}
          {arrival.state === 'seated' && hello === null && 'The table refused this session. Reload to try again.'}
          {arrival.state === 'seated' && hello !== null && hello !== 'pending' && hello !== 'retrying' && (
            `Connected · @${hello.user.handle ?? 'no handle yet'} · build ${hello.commit.slice(0, 7)}`
          )}
        </p>

        {arrival.state === 'unreachable' && (
          <button type="button" className="rounded border border-stone-500 px-3 py-1 hover:bg-stone-800" onClick={() => window.location.reload()}>
            Try again
          </button>
        )}
        {arrival.state === 'seated' && seat.state === 'loading' && <p data-testid="seat">Finding the table…</p>}
        {arrival.state === 'seated' && seat.state === 'error' && (
          <div className="space-y-2">
            <p data-testid="seat" role="alert">The table could not be reached just now. Try again in a moment.</p>
            <button type="button" className="rounded border border-stone-500 px-3 py-1 hover:bg-stone-800"
              onClick={async () => { setSeat({ state: 'loading' }); const r = await readTable(null); if (r) applyResult(r); }}>
              Try again
            </button>
          </div>
        )}
        {arrival.state === 'seated' && seat.state === 'not-found' && (
          <p data-testid="seat" role="alert">{watchRefused ? 'That watch link is no longer live. Ask the DM for a new one.' : 'Not found. There is no table at this address for you.'}</p>
        )}
        {arrival.state === 'seated' && seat.state === 'waiting' && (
          <p data-testid="seat" role="status">The DM hasn&apos;t started the session yet. You&apos;ll be let in as soon as they do; there&apos;s no need to reload.</p>
        )}
        {arrival.state === 'seated' && seat.state === 'removed' && (
          <p data-testid="seat" role="alert">{seat.gone ? `${seat.name} is gone: its campaign was deleted, and the table with it. Your own layout and dice are untouched.` : `You've been removed from ${seat.name}.`}</p>
        )}
        {arrival.state === 'seated' && (seat.state === 'not-found' || seat.state === 'waiting' || seat.state === 'removed') && (
          // The wrong account on this device (qa-runs#1469): these pages have no Table menu, so the way out is here.
          <p className="text-sm text-stone-400">
            Not you?{' '}
            <button type="button" data-testid="seat-sign-out" className="underline hover:text-stone-200"
              onClick={() => { setTokens(null); window.location.assign(siteTableUrl(campaignId)); }}>
              Sign out of the table
            </button>
            {' '}and come back through the site as the account you mean.
          </p>
        )}
        {arrival.state === 'seated' && seat.state === 'at' && (
          <Waiting view={seat.view} campaignId={campaignId} notice={notice} onResult={applyResult} />
        )}
      </section>
      {statement && <Onboarding onDone={async () => setSettings(await saveSettings({ onboardingSeen: true }))} />}
    </main>
  );
}

function withMembers(view: TableView, members: TableMember[]): TableView {
  const me = members.find((m) => m.id === view.me.id);
  return { ...view, members, me: me ? { ...view.me, colour: me.colour, colourLocked: me.colourLocked } : view.me };
}

/** The table before it is open, or while the socket is still joining: the DM opens it, a player waits. */
function Waiting({ view, campaignId, notice, onResult }: { view: TableView; campaignId: string; notice: string; onResult: (r: TableResult) => void }) {
  const isDm = view.me.role === 'dm';
  return (
    <div className="space-y-4" data-testid="seat">
      <p className="text-sm text-stone-400">
        {isDm ? 'You run this table.' : view.me.role === 'watcher' ? 'You are watching this table.' : view.me.role === 'codm' ? 'You are a co-DM at this table.' : 'You sit at this table.'}
        {!view.opened && (isDm ? ' It is not open yet.' : ' The DM has not opened it yet.')}
        {view.opened && ' Joining…'}
      </p>
      {notice && <p role="alert" className="text-amber-300 text-sm">{notice}</p>}
      {isDm && !view.opened && (
        <button type="button" className="vtt-btn-primary rounded bg-amber-600 px-3 py-1 text-stone-950 font-medium" onClick={async () => onResult(await (view.practice ? openPractice(campaignId) : openTable(campaignId)))}>
          Open table
        </button>
      )}
      <ul className="divide-y divide-stone-800" data-testid="members">
        {view.members.map((m) => (
          <li key={m.id} className="py-2 flex flex-wrap items-center gap-3 text-sm" data-testid={`member-${m.id}`}>
            <span className="inline-block h-4 w-4 rounded-full" style={{ backgroundColor: m.colour ?? '#444' }} aria-hidden="true" />
            <span>{memberLabel(m, {})}</span>
            <span className="text-stone-500">{roleWord(m.role)}{m.present ? ' · here' : ''}</span>
          </li>
        ))}
      </ul>
      {isDm && (
        <p className="text-xs text-stone-500">
          Inviting and removing people happens on the site:{' '}
          <a className="underline" href={`${siteUrl()}/campaigns`}>manage the campaign</a>.
        </p>
      )}
    </div>
  );
}
